import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { api, type ImportChunk, type ImportJob, type ImportPreview } from "@/lib/api";
import { useCan } from "@/lib/shell-context";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Mono,
  Panel,
  Select,
} from "@/components/primitives";

/**
 * CSV import (spec §8, ticket 25).
 *
 * Upload, confirm the column mapping against real rows, then run. The mapping
 * is confirmed rather than guessed because a wrong one would credit thousands
 * of players to the wrong influencer (decision 21). The file is sent in
 * chunks of 200 rows, each its own request, so the page has to stay open
 * until the bar reaches the end. A closed tab leaves a partial import; running
 * the file again completes it without duplicating players (each run does add
 * an "imported" line to their source history).
 */

/** The API accepts up to 4 MB of CSV per request. */
const MAX_BYTES = 4_000_000;

type Step =
  | { kind: "pick" }
  | {
      kind: "map";
      csv: string;
      filename: string;
      preview: ImportPreview;
      mapping: Record<string, string>;
    }
  | { kind: "run"; filename: string; progress: ImportChunk | null; total: number };

export function ImportPage() {
  const { botId } = useParams();
  const canEdit = useCan("import", "use");
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>({ kind: "pick" });
  const [error, setError] = useState<unknown>(null);

  const jobs = useQuery({
    queryKey: ["imports", botId],
    queryFn: () => api.get<ImportJob[]>(`/bots/${botId}/imports`),
  });

  const preview = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_BYTES) {
        throw new Error("That file is over 4 MB. Split it into smaller files and import each.");
      }
      const csv = await file.text();
      const result = await api.post<ImportPreview>(`/bots/${botId}/imports/preview`, {
        filename: file.name,
        csv,
      });
      return { csv, filename: file.name, result };
    },
    onSuccess: ({ csv, filename, result }) => {
      setError(null);
      setStep({ kind: "map", csv, filename, preview: result, mapping: { ...result.suggested } });
    },
    onError: setError,
  });

  const run = useMutation({
    mutationFn: async ({
      csv,
      mapping,
      filename,
      total,
    }: {
      csv: string;
      mapping: Record<string, string>;
      filename: string;
      total: number;
    }) => {
      // Only mapped fields are sent; an unmapped field is simply not imported.
      const chosen = Object.fromEntries(Object.entries(mapping).filter(([, header]) => header));
      let offset: number | null = 0;
      let last: ImportChunk | null = null;
      while (offset !== null) {
        last = await api.post<ImportChunk>(`/bots/${botId}/imports/run`, {
          csv,
          mapping: chosen,
          offset,
        });
        setStep({ kind: "run", filename, progress: last, total });
        offset = last.done ? null : last.nextOffset;
      }
      return last;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["imports", botId] });
      void queryClient.invalidateQueries({ queryKey: ["players", botId] });
    },
    onError: setError,
  });

  const reset = () => {
    setStep({ kind: "pick" });
    setError(null);
    run.reset();
    preview.reset();
    if (fileInput.current) fileInput.current.value = "";
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      {canEdit && (
        <Panel title="Import players from a CSV">
          {step.kind === "pick" && (
            <div className="grid gap-3 px-5 py-3">
              <p className="text-muted-foreground">
                One row per player. Only a Telegram user ID column is required; a source code, first
                seen date, names and username are used when present. Earlier dates win when a player
                already exists; their latest source is never changed.
              </p>
              <label className="grid gap-1.5">
                <span className="font-medium">CSV file</span>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".csv,text/csv"
                  disabled={preview.isPending}
                  className="block w-full text-muted-foreground file:mr-3 file:h-9 file:rounded-md file:border-0 file:bg-primary file:px-3 file:font-medium file:text-primary-foreground"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) preview.mutate(file);
                  }}
                />
              </label>
              {preview.isPending && (
                <span className="text-muted-foreground">Reading the file…</span>
              )}
            </div>
          )}

          {step.kind === "map" && (
            <MapStep
              step={step}
              onMapping={(mapping) => setStep({ ...step, mapping })}
              onBack={reset}
              onRun={() => {
                setError(null);
                setStep({
                  kind: "run",
                  filename: step.filename,
                  progress: null,
                  total: step.preview.totalRows,
                });
                run.mutate({
                  csv: step.csv,
                  mapping: step.mapping,
                  filename: step.filename,
                  total: step.preview.totalRows,
                });
              }}
            />
          )}

          {step.kind === "run" && (
            <RunStep step={step} running={run.isPending} failed={run.isError} onAgain={reset} />
          )}

          {error ? (
            <div className="px-5 pb-3">
              <ErrorNote error={error} />
            </div>
          ) : null}
        </Panel>
      )}

      <Panel title="Past imports">
        {jobs.isLoading ? (
          <Loading />
        ) : jobs.error ? (
          <div className="p-3.5">
            <ErrorNote error={jobs.error} />
          </div>
        ) : jobs.data && jobs.data.length === 0 ? (
          <Empty title="Nothing imported yet" hint="Imports from your old CRM appear here." />
        ) : (
          <ul className="divide-y divide-border/60">
            {jobs.data?.map((job) => (
              <JobRow key={job.id} job={job} />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function MapStep({
  step,
  onMapping,
  onBack,
  onRun,
}: {
  step: Extract<Step, { kind: "map" }>;
  onMapping: (mapping: Record<string, string>) => void;
  onBack: () => void;
  onRun: () => void;
}) {
  const { preview, mapping } = step;
  const missing = preview.fields.filter((f) => f.required && !mapping[f.field]);

  return (
    <div className="grid gap-4 px-5 py-3">
      <div className="text-muted-foreground">
        <span className="font-medium text-foreground">{step.filename}</span> ·{" "}
        <Mono>{preview.totalRows.toLocaleString()}</Mono> rows
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {preview.fields.map((f) => (
          <Field key={f.field} label={`${f.label}${f.required ? " (required)" : ""}`}>
            <Select
              className="w-full"
              value={mapping[f.field] ?? ""}
              onChange={(e) => onMapping({ ...mapping, [f.field]: e.target.value })}
            >
              <option value="">{f.required ? "Choose a column" : "Not in this file"}</option>
              {preview.headers.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </Select>
          </Field>
        ))}
      </div>

      <div className="grid gap-1.5">
        <div className="font-medium">First rows, as they will be read</div>
        {/* Arbitrary columns from someone else's CRM: this one table scrolls
            sideways on a phone rather than pretending to fit. */}
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr>
                {preview.fields
                  .filter((f) => mapping[f.field])
                  .map((f) => (
                    <th
                      key={f.field}
                      className="border-b border-border px-2.5 py-1.5 font-medium whitespace-nowrap text-muted-foreground"
                    >
                      {f.label}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {preview.sample.map((row, i) => (
                <tr key={i}>
                  {preview.fields
                    .filter((f) => mapping[f.field])
                    .map((f) => (
                      <td
                        key={f.field}
                        className="border-b border-border/60 px-2.5 py-1.5 whitespace-nowrap"
                      >
                        <Mono>{row[preview.headers.indexOf(mapping[f.field]!)] ?? ""}</Mono>
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button disabled={missing.length > 0} onClick={onRun}>
          Import {preview.totalRows.toLocaleString()} rows
        </Button>
        <Button variant="quiet" onClick={onBack}>
          Choose another file
        </Button>
        {missing.length > 0 && (
          <span className="text-destructive">
            Choose the column for {missing.map((f) => f.label).join(", ")}.
          </span>
        )}
      </div>
    </div>
  );
}

function RunStep({
  step,
  running,
  failed,
  onAgain,
}: {
  step: Extract<Step, { kind: "run" }>;
  running: boolean;
  failed: boolean;
  onAgain: () => void;
}) {
  const p = step.progress;
  const processed = p?.processed ?? 0;
  const pct = step.total > 0 ? Math.round((processed / step.total) * 100) : 0;

  return (
    <div className="grid gap-3 px-5 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <span className="font-medium">
          {running ? "Importing…" : failed ? "Stopped" : "Import finished"}
        </span>
        <span className="text-muted-foreground">{step.filename}</span>
        <span className="text-muted-foreground sm:ml-auto">
          <Mono>{processed.toLocaleString()}</Mono> / <Mono>{step.total.toLocaleString()}</Mono>
        </span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={pct}
      >
        <div className="h-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
      </div>
      {running && (
        <p className="text-amber-600 dark:text-amber-400">
          Keep this page open until it finishes. If it closes, import the same file again: players
          are never duplicated, though their source history gains a second “imported” line.
        </p>
      )}
      {p && (
        <div className="flex flex-wrap gap-x-5 gap-y-1">
          <span>
            <Mono>{p.created.toLocaleString()}</Mono> new players
          </span>
          <span>
            <Mono>{p.updated.toLocaleString()}</Mono> updated
          </span>
          <span>
            <Mono>{p.skipped.toLocaleString()}</Mono> skipped
          </span>
        </div>
      )}
      {p && p.errors.length > 0 && <ErrorList errors={p.errors} />}
      {!running && (
        <Button variant="quiet" className="justify-self-start" onClick={onAgain}>
          Import another file
        </Button>
      )}
    </div>
  );
}

const JOB_LABEL: Record<ImportJob["status"], string> = {
  preview: "Not started",
  running: "Incomplete",
  done: "Finished",
  failed: "Failed",
  cancelled: "Cancelled",
};

const JOB_TONE: Record<ImportJob["status"], string> = {
  preview: "text-muted-foreground",
  running: "text-amber-600 dark:text-amber-400",
  done: "text-ok",
  failed: "text-destructive",
  cancelled: "text-muted-foreground",
};

function JobRow({ job }: { job: ImportJob }) {
  const [open, setOpen] = useState(false);
  const errors = job.errors ?? [];

  return (
    <li className="grid grid-cols-1 gap-1.5 px-5 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className={`inline-flex items-center gap-1.5 ${JOB_TONE[job.status]}`}>
          <span className="size-1.5 rounded-full bg-current" aria-hidden />
          {JOB_LABEL[job.status]}
        </span>
        <span className="text-muted-foreground">
          {new Date(job.createdAt).toLocaleString()}
          {job.actor && <> · {job.actor}</>}
        </span>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-0.5">
        <span>
          <Mono>{job.processedRows.toLocaleString()}</Mono> of{" "}
          <Mono>{job.totalRows.toLocaleString()}</Mono> rows
        </span>
        <span>
          <Mono>{job.createdPlayers.toLocaleString()}</Mono> new
        </span>
        <span>
          <Mono>{job.updatedPlayers.toLocaleString()}</Mono> updated
        </span>
        <span>
          <Mono>{job.skippedRows.toLocaleString()}</Mono> skipped
        </span>
        {errors.length > 0 && (
          <Button variant="ghost" size="sm" className="sm:ml-auto" onClick={() => setOpen(!open)}>
            {open ? "Hide problems" : `Show ${errors.length} problems`}
          </Button>
        )}
      </div>
      {job.status === "running" && (
        <p className="text-muted-foreground">
          Did not finish. Import the same file again to complete it.
        </p>
      )}
      {open && <ErrorList errors={errors} />}
    </li>
  );
}

function ErrorList({ errors }: { errors: { row: number; reason: string }[] }) {
  return (
    <ul className="grid max-h-60 gap-1 overflow-y-auto rounded-md border border-border bg-muted/30 px-2.5 py-2">
      {errors.map((e, i) => (
        <li key={i} className="flex gap-3">
          <span className="shrink-0 text-muted-foreground">
            Row <Mono>{e.row}</Mono>
          </span>
          <span className="min-w-0 [overflow-wrap:anywhere]">{e.reason}</span>
        </li>
      ))}
    </ul>
  );
}
